import type { FC, PropsWithChildren, ReactNode } from 'react';

import { AppLogo, AppLogoEmergency } from 'features/app-logo/components';
import { useAppSelector } from 'features/app/hooks';
import styled, { styledProps } from 'features/theme/styled';
import theme from 'features/theme/theme';
import { selectIsConnected } from 'features/call/call-ui-state/store';

interface VrsBaseHeaderProps {
  callTimer: ReactNode;
  callId: ReactNode;
  isEmergency?: boolean;
}

export const VrsBaseHeader: FC<PropsWithChildren<VrsBaseHeaderProps>> = ({
  children,
  callTimer,
  callId,
  isEmergency = false,
}) => {
  const isCallInProgress = useAppSelector(selectIsConnected);

  return (
    <Container
      $isEmergency={isEmergency}
      data-testid='vrs-base-header'
      $isCallInProgress={isCallInProgress}
    >
      {isEmergency ? <AppLogoEmergency /> : <AppLogo />}
      {callTimer}
      {callId}
      <ControlPanel data-testid='call-controls-panel'>{children}</ControlPanel>
    </Container>
  );
};

const Container = styledProps('div')<{
  $isEmergency: boolean;
  $isCallInProgress: boolean;
}>`
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: end;
  flex-shrink: 0;
  padding: ${theme.spacing(0, 16)};
  background-color: ${theme.palette.grey[700]};
  height: 96px;
  border-bottom: 8px solid;
  border-color: ${({ $isCallInProgress }) =>
    $isCallInProgress
      ? theme.palette.primary[500]
      : theme.palette.deepChampagne[500]};

  ${({ $isEmergency }) =>
    $isEmergency &&
    `
  background-color: ${theme.palette.emergency.main};
  border-color: ${theme.palette.emergency.border};
  `};
`;

const ControlPanel = styled('div')`
  display: flex;
  margin-left: auto;
`;
